import { toast } from 'sonner';

import { getGhostKind } from '@/shared/lib/dev-ghost';
import { ghostMock } from '@/shared/lib/dev-ghost-mocks';

import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { ENV } from '@/shared/config/env';
import { logger } from '@/shared/lib/logger';
import { sentryHelpers } from '@/shared/lib/sentry';
import { supabaseAuthService } from '@/shared/services/supabase-auth-service';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: ENV.API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        // Add app source header
        config.headers['X-App-Source'] = ENV.APP_PROJECT;

        // DEV ghost mode: short-circuit known GETs with mock data via a custom
        // adapter (no network, no token needed) so data pages render for review.
        // Tree-shaken from prod (import.meta.env.DEV inside ghostMock).
        const mock = ghostMock(config);
        if (mock !== undefined) {
          config.adapter = () =>
            Promise.resolve({
              data: mock,
              status: 200,
              statusText: 'OK',
              headers: {},
              config,
            });
          return config;
        }

        // For FormData bodies, let the browser set Content-Type with the
        // multipart boundary. The instance default 'application/json' would
        // otherwise be inherited and break uploads (refs-service,
        // admin-assets-service) — Multer would parse the body as JSON and
        // report "No file provided" (HTTP 400).
        if (config.data instanceof FormData) {
          if (config.headers && typeof (config.headers as { delete?: (k: string) => void }).delete === 'function') {
            (config.headers as unknown as { delete: (k: string) => void }).delete('Content-Type');
          } else if (config.headers) {
            delete (config.headers as Record<string, unknown>)['Content-Type'];
          }
        }

        // Check if we should skip token attachment (used during auth initialization)
        const skipInterceptor = (config as { _skipInterceptor?: boolean })._skipInterceptor;

        if (!skipInterceptor) {
          // Get token from Supabase session
          let token = await supabaseAuthService.getAccessToken();

          // If no token, try to refresh the session
          if (!token) {
            logger.api.debug('No token available, attempting to refresh session', {
              url: config.url,
            });
            try {
              const { session } = await supabaseAuthService.refreshSession();
              if (session) {
                token = session.access_token;
                logger.api.debug('Session refreshed successfully', { url: config.url });
              }
            } catch (refreshError) {
              logger.api.warn('Failed to refresh session in request interceptor', {
                error: refreshError,
              });
            }
          }

          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            // Log token acquisition for debugging
            logger.api.debug('API Request: Token acquired for', {
              url: config.url,
              tokenLength: token.length,
            });
          } else {
            logger.api.debug('API Request: No token available for', { url: config.url });
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        // A request its caller aborted is not a failure: React Query aborts a
        // superseded refetch through the query's AbortSignal (playground
        // history), uploads abort on cancel. No toast, no Sentry event.
        if (axios.isCancel(error)) return Promise.reject(error);

        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        // Handle 401 errors - simplified token refresh
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          logger.api.debug('Got 401, attempting token refresh', { url: originalRequest.url });
          originalRequest._retry = true;

          try {
            const { session } = await supabaseAuthService.refreshSession();

            if (session) {
              logger.api.debug('Token refresh successful, retrying request');
              // Retry with new token
              if (!originalRequest.headers) {
                originalRequest.headers = {};
              }
              originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
              return this.client(originalRequest);
            } else {
              logger.api.error('Token refresh returned no session');
            }
          } catch (refreshError) {
            logger.api.error('Token refresh failed', { error: refreshError });
            // Refresh failed - sign out and redirect
            await this.handleAuthFailure();
          }
        }

        // Note: Removed the check for /auth/me returning authenticated: false
        // This should be handled by the AuthProvider, not the interceptor

        // Silently reject 400 and 409 — callers handle these with specific error messages
        if (error.response?.status === 400 || error.response?.status === 409) {
          return Promise.reject(error);
        }

        // Report non-auth errors to Sentry
        if (error.response?.status !== 401) {
          sentryHelpers.captureError(
            error instanceof Error ? error : new Error('API request failed'),
            {
              api: {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                data: error.response?.data,
              },
            },
          );
        }

        // Suppress global toast for callers that opted into silent failures
        // (e.g. background queries that surface their own error UI).
        // DEV ghost mode browses with no token — every call 401s; a toast per
        // failed call buries the UI. Ghost is for looking, so mute them all.
        const ghostMuted = import.meta.env.DEV && getGhostKind() !== null;
        const silent =
          (error.config as { _silent?: boolean } | undefined)?._silent === true;
        if (!silent && !ghostMuted) {
          if (error.response?.data && typeof error.response.data === 'object') {
            // PR-2: V1 surface emits { error: { type, code, message, ... } };
            // legacy NestJS routes still emit { message } directly. Read the
            // V1 envelope first, fall back to the flat shape so dashboard
            // surfaces (admin, billing, etc.) keep working unchanged.
            const errorData = error.response.data as { message?: string; error?: { message?: string } };
            const message = errorData.error?.message ?? errorData.message ?? 'Something went wrong';
            toast.error(message);
          } else {
            toast.error('Network error');
          }
        }

        return Promise.reject(error);
      },
    );
  }

  private async handleAuthFailure() {
    // Sign out via Supabase
    await supabaseAuthService.signOut();

    // Clear auth store
    const authStore = (await import('@/features/auth/model/auth-store')).useAuthStore.getState();
    authStore.logout();

    // Redirect to register page
    if (typeof window !== 'undefined' && window.location.pathname !== '/auth/register') {
      window.location.href = '/auth/register';
    }
  }

  get instance() {
    return this.client;
  }
}

export const apiClient = new ApiClient().instance;
